import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { sanitizeInput, formatCuit } from '../../utils/inputValidation';
import { getScoringListing, resetCase } from '../../services/userservice';
import HondaLoader from '../../components/common/HondaLoader';
import Swal from 'sweetalert2';

const Casos = ({ user, onLogout, onNavigate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('rechazado');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    type: '', // 'deshacer' o 'avanzar'
    caso: null
  });
  const [data, setData] = useState([]);
  const [todosLosCasos, setTodosLosCasos] = useState([]);
  const [loading, setLoading] = useState(true);

  // Función para formatear fecha de yyyy-mm-dd a dd-mm-yyyy
  const formatearFecha = (fecha) => {
    if (!fecha || fecha === 'N/A') {
      return 'N/A';
    }
    
    try {
      // Si ya está en formato dd-mm-yyyy o dd/mm/yyyy, convertir
      if (fecha.includes('/')) {
        const partes = fecha.split('/');
        if (partes.length === 3) {
          const [dia, mes, anio] = partes;
          return `${dia}-${mes}-${anio}`;
        }
      }
      
      // Si está en formato yyyy-mm-dd, convertir a dd-mm-yyyy
      if (fecha.includes('-')) {
        const partes = fecha.split('-');
        if (partes.length === 3) {
          // Verificar si es yyyy-mm-dd o dd-mm-yyyy
          if (partes[0].length === 4) {
            // Es yyyy-mm-dd
            const [anio, mes, dia] = partes;
            return `${dia}-${mes}-${anio}`;
          } else {
            // Ya está en dd-mm-yyyy
            return fecha;
          }
        }
      }
      
      return fecha;
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return fecha;
    }
  };

  // Cargar datos desde el backend al montar el componente
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Obtener casos INCOMPLETOS, RECHAZADOS y APROBADOS
        const [incompletos, rechazados, aprobados] = await Promise.all([
          getScoringListing('INCOMPLETOS', user.ce_ID),
          getScoringListing('RECHAZADOS', user.ce_ID),
          getScoringListing('FINALIZADO APTO', user.ce_ID),
        ]);
        
        // Mapear ambos arrays al formato esperado
        const mapearCasos = (casos, estado) => casos.map((item, index) => {
          const fechaAceptacion = item.acceptDate || item.aceptacion || 'N/A';
          return {
            id: item.id || index + 1,
            grupo: item.group || item.grupo || 'N/A',
            orden: item.order || item.orden || 'N/A',
            titular: item.mainName || item.titularName || item.titular || 'N/A',
            cuil: item.cuil || item.cuit || 'N/A',
            aceptacion: formatearFecha(fechaAceptacion),
            porcentajeDeuda: parseFloat(item.debtPercent || item.porcentajeDeuda || 0),
            concesionario: item.sellerHouse.ceName,
            fechaEstado: item.statusDate || item.fechaEstado || 'N/A',
            estado: estado
          };
        });
        
        const casosIncompletos = mapearCasos(incompletos, 'incompleto');
        const casosRechazados = mapearCasos(rechazados, 'rechazado');
        const casosAprobados = mapearCasos(aprobados, 'aprobado');
        
        // Combinar ambos arrays
        const todosCasos = [...casosIncompletos, ...casosRechazados, ...casosAprobados];
        setTodosLosCasos(todosCasos);
        
      } catch (err) {
        console.error('Error cargando casos:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error al cargar datos',
          text: 'No se pudieron cargar los casos. Por favor intente nuevamente.',
          confirmButtonColor: '#ef4444',
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Actualizar data cuando cambie el filtro de estado o todosLosCasos
  useEffect(() => {
    const casosFiltrados = todosLosCasos.filter(caso => caso.estado === estadoFilter);
    setData(casosFiltrados);
  }, [estadoFilter, todosLosCasos]);

  const getEstadoInfo = (estado) => {
    switch(estado) {
      case 'incompleto':
        return { text: 'Incompleto', color: 'bg-yellow-100 text-yellow-700' };
      case 'rechazado':
        return { text: 'Rechazado', color: 'bg-red-100 text-red-700' };
      case 'aprobado':
        return { text: 'Aprobado', color: 'bg-green-100 text-green-700' };
      default:
        return { text: 'Desconocido', color: 'bg-gray-100 text-gray-700' };
    }
  };

  const getFilterText = () => {
    
    switch(estadoFilter) {
      case 'incompleto':
        return 'Incompletos';
      case 'rechazado':
        return 'Rechazados';
      case 'aprobado':
        return 'Aprobados';
      default:
        return 'Seleccionar estado';
    }
  };

  const getFilterColor = () => {
    switch(estadoFilter) {
      case 'incompleto':
        return 'bg-yellow-500';
      case 'rechazado':
        return 'bg-red-500';
      case 'aprobado':
        return 'bg-green-500';
      default:
        return 'bg-gray-400';
    }
  };

  const filteredData = data.filter(row => {
    const matchesSearch = 
      row.grupo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.orden.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.titular.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.cuil.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.concesionario.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  const handleInfoClick = (row) => {
    onNavigate('visualizacionExpediente', { 
      id: row.id, 
      grupo: row.grupo, 
      orden: row.orden,
      titular: row.titular,
      cuil: row.cuil
    });
  };

  const handleDeshacerClick = (row) => {
    setModalConfig({
      isOpen: true,
      type: 'deshacer',
      caso: row
    });
  };

  const handleAvanzarClick = (row) => {
    setModalConfig({
      isOpen: true,
      type: 'avanzar',
      caso: row
    });
  };

  const handleConfirmarAccion = async () => {
    if (modalConfig.type === 'deshacer') {
      try {
        // Llamar al endpoint para resetear el caso
        await resetCase(modalConfig.caso.grupo, modalConfig.caso.orden);
        
        // Mostrar mensaje de éxito
        Swal.fire({
          icon: 'success',
          title: 'Caso deshecho',
          text: `El caso de ${modalConfig.caso.titular} ha sido deshecho exitosamente.`,
          confirmButtonColor: '#22c55e',
        });
        
        // Recargar los datos
        const [incompletos, rechazados, aprobados] = await Promise.all([
          getScoringListing('INCOMPLETOS', user.ce_ID),
          getScoringListing('RECHAZADOS', user.ce_ID),
          getScoringListing('FINALIZADO APTO', user.ce_ID)
        ]);
        
        const mapearCasos = (casos, estado) => casos.map((item, index) => {
          const fechaAceptacion = item.acceptDate || item.aceptacion || 'N/A';
          return {
            id: item.id || index + 1,
            grupo: item.group || item.grupo || 'N/A',
            orden: item.order || item.orden || 'N/A',
            titular: item.mainName || item.titularName || item.titular || 'N/A',
            cuil: item.cuil || item.cuit || 'N/A',
            aceptacion: formatearFecha(fechaAceptacion),
            porcentajeDeuda: parseFloat(item.debtPercent || item.porcentajeDeuda || 0),
            concesionario: '01050_MAIPU',
            fechaEstado: item.statusDate || item.fechaEstado || 'N/A',
            estado: estado
          };
        });
        
        const casosIncompletos = mapearCasos(incompletos, 'incompleto');
        const casosRechazados = mapearCasos(rechazados, 'rechazado');
        const casosAprobados = mapearCasos(aprobados, 'aprobado');
        const todosCasos = [...casosIncompletos, ...casosRechazados, ...casosAprobados];
        setTodosLosCasos(todosCasos);
        
        // Cerrar modal
        setModalConfig({ isOpen: false, type: '', caso: null });
      } catch (error) {
        console.error('Error al deshacer caso:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo deshacer el caso. Por favor intente nuevamente.',
          confirmButtonColor: '#ef4444',
        });
      }
    } else if (modalConfig.type === 'avanzar') {
      // Preparar datos para análisis crediticio
      const scoringData = {
        grupo: modalConfig.caso.grupo,
        orden: modalConfig.caso.orden,
        titular: modalConfig.caso.titular,
        cuil: modalConfig.caso.cuil,
        aceptacion: modalConfig.caso.aceptacion,
        concesionario: modalConfig.caso.concesionario,
        porcentajeDeuda: modalConfig.caso.porcentajeDeuda,
        estado: modalConfig.caso.estado,
        fromCasos: true, // Indica que viene de la pantalla de casos (ya está guardado)
        idScoring: modalConfig.caso.id // ID del scoring existente
      };
      
      // Guardar en localStorage para que AnalisisCrediticio lo recupere
      localStorage.setItem('scoringData', JSON.stringify(scoringData));
      
      // Cerrar modal
      setModalConfig({ isOpen: false, type: '', caso: null });
      
      // Navegar a análisis crediticio con los datos del caso
      onNavigate('adminAnalisisCrediticio', {
        grupo: modalConfig.caso.grupo,
        orden: modalConfig.caso.orden,
        estado: modalConfig.caso.estado
      });
    }
  };

  const handleCancelarAccion = () => {
    setModalConfig({ isOpen: false, type: '', caso: null });
  };

  const getPorcentajeColor = (porcentaje) => {
    if (porcentaje > 66) return 'bg-red-100 text-red-700';
    if (porcentaje > 33) return 'bg-yellow-100 text-yellow-700';
    return 'bg-green-100 text-green-700';
  };

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.dropdown-container')) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  if (loading) {
    return (
      <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
        <div className="flex items-center justify-center h-screen">
          <HondaLoader />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
      <div className="max-w-full mx-auto px-2 xl:px-3 py-3 xl:py-4">
        {/* Título - Optimizado para 1366x768 */}
        <div className="mb-5 xl:mb-8">
          <div className="flex items-center gap-2 xl:gap-3 mb-1 xl:mb-2">
            <div className="w-1 xl:w-1.5 h-6 xl:h-8 gradient-red rounded-full shadow-glow-red"></div>
            <h2 className="text-xl xl:text-2xl font-bold text-gradient">
              Control de Casos
            </h2>
          </div>
          <p className="text-gray-500 text-xs xl:text-sm ml-4 xl:ml-5">Gestione y administre los casos incompletos y rechazados</p>
        </div>

        <div className="mb-3 xl:mb-4 flex flex-col sm:flex-row gap-3 xl:gap-4">
          <div className="relative flex-1 max-w-md">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center">
              <span className="material-icons text-gray-400 text-lg">search</span>
            </span>
            <input
              type="text"
              placeholder="Buscar por Grupo, Orden, Titular, DNI o Concesionario..."
              value={searchTerm}
              onChange={(e) => {
                const sanitized = sanitizeInput(e.target.value);
                setSearchTerm(sanitized);
              }}
              className="w-full pl-10 pr-4 py-1.5 xl:py-2 border border-gray-200 rounded-md text-gray-700 text-xs xl:text-sm focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
            />
          </div>

          <div className="relative dropdown-container">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center justify-between gap-2 px-3 xl:px-4 py-1.5 xl:py-2 bg-white border border-gray-200 rounded-md text-xs xl:text-sm text-gray-700 hover:bg-gray-50 transition min-w-[160px] xl:min-w-[180px]"
            >
              <span className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${getFilterColor()}`}></div>                
                {getFilterText()}
              </span>
              <span className="material-icons text-gray-400 text-base">
                {isDropdownOpen ? 'expand_less' : 'expand_more'}
              </span>
            </button>
            
            {isDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg z-10 overflow-hidden">

                <button
                  onClick={() => {
                    setEstadoFilter('incompleto');
                    setIsDropdownOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm transition ${
                    estadoFilter === 'incompleto'
                      ? 'bg-yellow-50 text-yellow-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                    Incompletos
                  </span>
                </button>

                <button
                  onClick={() => {
                    setEstadoFilter('rechazado');
                    setIsDropdownOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm transition ${
                    estadoFilter === 'rechazado'
                      ? 'bg-red-50 text-red-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                    Rechazados
                  </span>
                </button>

                <button
                  onClick={() => {
                    setEstadoFilter('aprobado');
                    setIsDropdownOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm transition ${
                    estadoFilter === 'aprobados'
                      ? 'bg-green-50 text-green-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    Aprobados
                  </span>
                </button>


              </div>
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-red-600 to-red-700">
                <tr>
                  <th className="px-2 xl:px-4 py-2 xl:py-4 text-left text-[10px] xl:text-xs font-bold text-white uppercase tracking-wider">Grupo</th>
                  <th className="px-2 xl:px-4 py-2 xl:py-4 text-left text-[10px] xl:text-xs font-bold text-white uppercase tracking-wider">Orden</th>
                  <th className="px-2 xl:px-4 py-2 xl:py-4 text-left text-[10px] xl:text-xs font-bold text-white uppercase tracking-wider">Titular</th>
                  <th className="px-2 xl:px-4 py-2 xl:py-4 text-left text-[10px] xl:text-xs font-bold text-white uppercase tracking-wider">DNI / CUIT</th>
                  <th className="px-2 xl:px-4 py-2 xl:py-4 text-left text-[10px] xl:text-xs font-bold text-white uppercase tracking-wider">Aceptación</th>
                  <th className="px-2 xl:px-4 py-2 xl:py-4 text-center text-[10px] xl:text-xs font-bold text-white uppercase tracking-wider">Deuda</th>
                  <th className="px-2 xl:px-4 py-2 xl:py-4 text-left text-[10px] xl:text-xs font-bold text-white uppercase tracking-wider">Concesionario</th>
                  <th className="px-2 xl:px-4 py-2 xl:py-4 text-left text-[10px] xl:text-xs font-bold text-white uppercase tracking-wider">Estado</th>
                  <th className="px-2 xl:px-4 py-2 xl:py-4 text-center text-[10px] xl:text-xs font-bold text-white uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredData.length > 0 ? (
                  filteredData.map((row) => {
                    const estadoInfo = getEstadoInfo(row.estado);
                    
                    return (
                      <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-2 xl:px-4 py-2 xl:py-3 text-[11px] xl:text-sm text-gray-900">{row.grupo}</td>
                        <td className="px-2 xl:px-4 py-2 xl:py-3 text-[11px] xl:text-sm text-gray-900">{row.orden}</td>
                        <td className="px-2 xl:px-4 py-2 xl:py-3 text-[11px] xl:text-sm text-gray-900">{row.titular}</td>
                        <td className="px-2 xl:px-4 py-2 xl:py-3 text-[11px] xl:text-sm text-gray-900">{formatCuit(row.cuil)}</td>
                        <td className="px-2 xl:px-4 py-2 xl:py-3 text-[11px] xl:text-sm text-gray-900">{row.aceptacion}</td>
                        <td className="px-2 xl:px-4 py-2 xl:py-3 text-[11px] xl:text-sm text-center">
                          <span className={`inline-flex px-1.5 xl:px-2 py-0.5 xl:py-1 rounded-full text-[10px] xl:text-xs ${getPorcentajeColor(row.porcentajeDeuda)}`}>
                            {row.porcentajeDeuda}%
                          </span>
                        </td>
                        <td className="px-2 xl:px-4 py-2 xl:py-3 text-[11px] xl:text-sm text-gray-900">{row.concesionario}</td>
                        <td className="px-2 xl:px-4 py-2 xl:py-3 text-[11px] xl:text-sm text-gray-900">
                          {estadoInfo.text}
                        </td>
                        <td className="px-2 xl:px-4 py-2 xl:py-3 text-center">
                          <div className="flex items-center justify-center gap-1 xl:gap-2">

                            {/* Botón de info comentado temporalmente para casos incompletos */}
                            {estadoFilter !== 'incompleto' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleInfoClick(row);
                                }}
                                className="text-blue-600 hover:bg-blue-600 hover:text-white transition-colors p-0.5 xl:p-1 rounded-full"
                                title="Ver información"
                              >
                                <span className="material-icons text-lg xl:text-xl">info</span>
                              </button>
                            )}
                            
                            {(estadoFilter == 'incompleto' || estadoFilter == 'rechazado' ) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeshacerClick(row);
                                }}
                                className="text-red-600 hover:bg-red-600 hover:text-white transition-colors p-0.5 xl:p-1 rounded-full"
                                title="Deshacer caso"
                              >
                                <span className="material-icons text-lg xl:text-xl">undo</span>
                              </button>
                            )}

                            {(estadoFilter == 'incompleto' || estadoFilter == 'rechazado' ) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAvanzarClick(row);
                                }}
                                className="text-green-600 hover:bg-green-600 hover:text-white transition-colors p-0.5 xl:p-1 rounded-full"
                                title="Avanzar caso"
                              >
                                <span className="material-icons text-lg xl:text-xl">arrow_forward</span>
                              </button>
                            )}

                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                      No se encontraron resultados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 text-right text-xs text-gray-500">
          Mostrando {filteredData.length} de {data.length} registros
        </div>
      </div>

      {/* Modal de Confirmación */}
      {modalConfig.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden animate-fadeIn">
            {/* Header */}
            <div className={`px-6 py-4 ${
              modalConfig.type === 'deshacer' 
                ? 'bg-gradient-to-r from-red-600 to-red-700' 
                : 'bg-gradient-to-r from-green-600 to-green-700'
            }`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white/20 backdrop-blur-sm">
                  <span className="material-icons text-2xl text-white">
                    {modalConfig.type === 'deshacer' ? 'undo' : 'arrow_forward'}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-white">
                  {modalConfig.type === 'deshacer' ? 'Deshacer Caso' : 'Avanzar Caso'}
                </h3>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              <p className="text-gray-700 mb-4">
                {modalConfig.type === 'deshacer' 
                  ? '¿Está seguro que desea deshacer este caso?' 
                  : '¿Está seguro que desea avanzar este caso?'}
              </p>
              
              {modalConfig.caso && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">Titular:</span>
                    <span className="text-sm text-gray-800 font-semibold">{modalConfig.caso.titular}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">Grupo:</span>
                    <span className="text-sm text-gray-800">{modalConfig.caso.grupo}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">Orden:</span>
                    <span className="text-sm text-gray-800">{modalConfig.caso.orden}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">DNI:</span>
                    <span className="text-sm text-gray-800 font-mono">{modalConfig.caso.cuil}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={handleCancelarAccion}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmarAccion}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-all shadow-sm hover:shadow-md ${
                  modalConfig.type === 'deshacer'
                    ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800'
                    : 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800'
                }`}
              >
                {modalConfig.type === 'deshacer' ? 'Deshacer' : 'Avanzar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default Casos;

